import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Image,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  createStyles,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import dayjs from "dayjs";
import {
  useGetSubmissionDetail,
  useReviewSubmission,
} from "../services/api/SupervisorClockSubmissionApiService";
import CapabilityProtectedComponent from "../../../common/uac/CapabilityProtectedComponent";
import { CAPABILITY_TA_BULK_CLOCK_APPROVE } from "../../../utils/UserAccessControlUtils";

const useStyles = createStyles(() => ({
  photoContainer: {
    borderRadius: 8,
    overflow: "hidden",
    maxHeight: 300,
  },
  infoRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 16,
  },
  employeeList: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "8px 16px",
  },
}));

interface SupervisorClockReviewModalProps {
  opened: boolean;
  submissionId: string | null;
  locationId: string | undefined;
  onClose: () => void;
}

type ModalStep = "review" | "confirmation";

const SupervisorClockReviewModal: React.FC<SupervisorClockReviewModalProps> = ({
  opened,
  submissionId,
  locationId,
  onClose,
}) => {
  const { classes } = useStyles();
  const isPhone = useMediaQuery("(max-width: 639px)");
  const [step, setStep] = useState<ModalStep>("review");
  const [decisions, setDecisions] = useState<Record<string, boolean>>({});

  const { data: submission, isLoading } = useGetSubmissionDetail(
    opened ? submissionId : null
  );
  const reviewMutation = useReviewSubmission(locationId);

  // Initialize all employees as approved when data loads
  useEffect(() => {
    if (submission?.employees) {
      const initial: Record<string, boolean> = {};
      submission.employees.forEach((emp) => {
        initial[emp.id] = true;
      });
      setDecisions(initial);
      setStep("review");
    }
  }, [submission]);

  const approvedEmployees = useMemo(
    () =>
      submission?.employees.filter((emp) => decisions[emp.id] === true) ?? [],
    [submission, decisions]
  );

  const declinedEmployees = useMemo(
    () =>
      submission?.employees.filter((emp) => decisions[emp.id] === false) ?? [],
    [submission, decisions]
  );

  const allChecked = useMemo(
    () => Object.values(decisions).every(Boolean),
    [decisions]
  );

  const handleToggleAll = (): void => {
    const newValue = !allChecked;
    const updated: Record<string, boolean> = {};
    Object.keys(decisions).forEach((id) => {
      updated[id] = newValue;
    });
    setDecisions(updated);
  };

  const handleToggleEmployee = (employeeId: string): void => {
    setDecisions((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  const handleSaveReview = (): void => {
    setStep("confirmation");
  };

  const handleConfirm = (): void => {
    if (!submissionId) return;

    const reviewDecisions: Record<string, "approved" | "declined"> = {};
    Object.entries(decisions).forEach(([id, approved]) => {
      reviewDecisions[id] = approved ? "approved" : "declined";
    });

    reviewMutation.mutate(
      { submissionId, request: { decisions: reviewDecisions } },
      {
        onSuccess: () => {
          showNotification({
            title: "Review submitted",
            message: `${approvedEmployees.length} approved, ${declinedEmployees.length} declined.`,
            color: "green",
          });
          onClose();
        },
        onError: () => {
          showNotification({
            title: "Error",
            message: "Failed to submit review. Please try again.",
            color: "red",
          });
        },
      }
    );
  };

  const handleClose = (): void => {
    setStep("review");
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text weight={600} size="lg">
          Approve employee clock in by Supervisor on site
        </Text>
      }
      size={isPhone ? "100%" : "lg"}
      closeOnClickOutside={false}
      centered={!isPhone}
      styles={
        isPhone
          ? {
              modal: { height: "100vh", maxHeight: "100vh", borderRadius: 0 },
            }
          : undefined
      }
    >
      <LoadingOverlay visible={isLoading || reviewMutation.isLoading} />

      {step === "review" && submission && (
        <Stack spacing="md">
          {/* Verification Photo */}
          {submission.verification_photo_url && (
            <div className={classes.photoContainer}>
              <Image
                src={submission.verification_photo_url}
                alt="Verification photo"
                fit="contain"
                height={280}
                radius="md"
              />
            </div>
          )}

          {/* Submission Info */}
          <div className={classes.infoRow}>
            <div>
              <Text size="xs" color="dimmed">
                Date/Time:
              </Text>
              <Text size="sm">
                {dayjs(submission.submitted_at).format("DD-MM-YY HH:mm")}
              </Text>
            </div>
            <div>
              <Text size="xs" color="dimmed">
                Supervisor:
              </Text>
              <Text size="sm">{submission.supervisor_name}</Text>
            </div>
          </div>

          <Divider />

          {/* Employee Selection */}
          <Checkbox
            label={`Approve Employees (${submission.employees.length}):`}
            checked={allChecked}
            onChange={handleToggleAll}
            styles={{
              label: { fontWeight: 600, fontSize: 14 },
            }}
          />

          <div className={classes.employeeList}>
            {submission.employees.map((emp) => (
              <Checkbox
                key={emp.id}
                label={emp.employee_name}
                checked={decisions[emp.id] ?? true}
                onChange={() => handleToggleEmployee(emp.id)}
                size="sm"
              />
            ))}
          </div>

          {/* Actions */}
          <CapabilityProtectedComponent
            requiredCapability={CAPABILITY_TA_BULK_CLOCK_APPROVE}
          >
            <div className="flex flex-col-reverse gap-xs mt-md sm:flex-row sm:justify-end sm:gap-sm">
              <Button
                variant="subtle"
                onClick={handleClose}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveReview}
                disabled={approvedEmployees.length === 0}
                className="w-full sm:w-auto"
              >
                Save Changes
              </Button>
            </div>
          </CapabilityProtectedComponent>
        </Stack>
      )}

      {step === "confirmation" && submission && (
        <Stack spacing="md">
          {/* Submission Info */}
          <div>
            <Text size="xs" color="dimmed">
              Date/Time:
            </Text>
            <Text size="sm">
              {dayjs(submission.submitted_at).format("DD-MM-YY HH:mm")}
            </Text>
          </div>
          <div>
            <Text size="xs" color="dimmed">
              Supervisor:
            </Text>
            <Text size="sm">{submission.supervisor_name}</Text>
          </div>

          {/* Approved Employees */}
          {approvedEmployees.length > 0 && (
            <div>
              <Text size="sm" weight={600} mb={4}>
                Approved Employees ({approvedEmployees.length}):
              </Text>
              {approvedEmployees.map((emp) => (
                <Text key={emp.id} size="sm">
                  {emp.employee_name}
                </Text>
              ))}
            </div>
          )}

          {/* Declined Employees */}
          {declinedEmployees.length > 0 && (
            <div>
              <Text size="sm" weight={600} mb={4}>
                Declined Employees ({declinedEmployees.length}):
              </Text>
              {declinedEmployees.map((emp) => (
                <Text key={emp.id} size="sm">
                  {emp.employee_name}
                </Text>
              ))}
            </div>
          )}

          {/* Actions */}
          <CapabilityProtectedComponent
            requiredCapability={CAPABILITY_TA_BULK_CLOCK_APPROVE}
          >
            <div className="flex flex-col-reverse gap-xs mt-md sm:flex-row sm:justify-end sm:gap-sm">
              <Button
                variant="subtle"
                onClick={() => setStep("review")}
                className="w-full sm:w-auto"
              >
                Go Back
              </Button>
              <Button
                onClick={handleConfirm}
                loading={reviewMutation.isLoading}
                className="w-full sm:w-auto"
              >
                Save Changes
              </Button>
            </div>
          </CapabilityProtectedComponent>
        </Stack>
      )}
    </Modal>
  );
};

export default SupervisorClockReviewModal;
